import { cn } from "@/shared/utils";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { PropsWithChildren } from "react";

function Collapsible({ ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
    return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

function CollapsibleTrigger({ ...props }: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>) {
    return <CollapsiblePrimitive.CollapsibleTrigger data-slot="collapsible-trigger" {...props} />;
}

/**
 *
 * @param preventFocusClipping If true, applies extra width and negative margin to prevent input focus outlines from being clipped by the overflow hidden that is necesary for the animation.
 */
function CollapsibleContent({
    className,
    preventFocusClipping = false,
    ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent> & {
    preventFocusClipping?: boolean;
}) {
    return (
        <CollapsiblePrimitive.CollapsibleContent
            className={cn(
                "data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden",
                preventFocusClipping && "-ml-1 w-[calc(100%+8px)] px-1 py-1",
                className
            )}
            data-slot="collapsible-content"
            {...props}
        />
    );
}

type SimpleCollapsibleContent = PropsWithChildren<{
    open: boolean;
    preventFocusClipping: boolean;
    className?: string;
}>;

/**
 * Enables smooth auto-height transitions via grid-template-rows animation.
 * Use this instead of the collapsible component if it has issues with interactive elements (e.g. buttons, checkboxes) in the trigger.
 *
 * @param preventFocusClipping If true, applies extra width and negative margin to prevent input focus outlines from being clipped by the overflow hidden that is necesary for the animation.
 */
function SimpleCollapsibleContent({ open, preventFocusClipping, children, className }: SimpleCollapsibleContent) {
    return (
        <div
            className={cn(
                "grid [grid-template-rows:minmax(0,_0fr)] transition-[grid-template-rows] duration-300 ease-out",
                open && "[grid-template-rows:minmax(0,_1fr)]"
            )}
            data-slot="simple-collapsible-content"
        >
            <div className={cn("row-span-2 row-start-1 overflow-hidden")}>
                <div className={cn(preventFocusClipping && "-ml-1 w-[calc(100%+8px)] px-2 py-1", className)}>{children}</div>
            </div>
        </div>
    );
}

export { Collapsible, CollapsibleContent, CollapsibleTrigger, SimpleCollapsibleContent };
